// WebCodecs video export pipeline
// demux+decode (mediabunny Input/VideoSampleSink) → scale letterbox (OffscreenCanvas) → encode (VideoEncoder) → mux (mediabunny)
// Each segment is flushed and muxed immediately to keep memory bounded across many segments.
// Long slices are sub-chunked to reset the hardware decoder and prevent GPU memory exhaustion.
import { debug } from 'debug'
import {
    Input,
    ALL_FORMATS,
    BlobSource,
    VideoSampleSink,
    Output,
    Mp4OutputFormat,
    BufferTarget,
    EncodedVideoPacketSource,
    EncodedPacket,
} from 'mediabunny'
import type { VideoSlice } from '../../models3/ProjectModels'

const log = debug('sltt:WebCodecsExporter') 

const OUT_W = 1280
const OUT_H = 720
const OUT_FPS = 30
const OUT_VIDEO_BPS = 2_500_000
const GOP_SIZE = OUT_FPS * 2
const TRIM_EPSILON = 0.001
// Chrome's hardware decoder can exhaust GPU resources on long continuous sessions.
// Sub-chunking resets the decoder every N seconds.
const MAX_DECODE_CHUNK_SEC = 30

export type ExportProgressFn = (pct: number, message: string) => void

export function webCodecsSupported(): boolean {
    return typeof VideoDecoder !== 'undefined' && typeof VideoEncoder !== 'undefined'
}

type ChunkEntry = { chunk: EncodedVideoChunk; meta: EncodedVideoChunkMetadata | undefined }

async function decodeWindow(
    blob: Blob,
    windowStart: number,
    windowEnd: number,
    encoder: VideoEncoder,
    baseTimestampUs: number,
    canvas: OffscreenCanvas,
    ctx: OffscreenCanvasRenderingContext2D,
    forceKeyFrame: boolean,
    onProgress: ExportProgressFn,
    sliceIndex: number,
    sliceCount: number,
    sliceStart: number,
    sliceEnd: number,
    windowIndex: number,
    windowCount: number,
): Promise<number> {
    const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(blob) })

    const videoTrack = await input.getPrimaryVideoTrack()
    if (!videoTrack) throw new Error('No video track found in file')

    const canDecode = await videoTrack.canDecode()
    if (!canDecode) throw new Error('Video track codec is not decodable in this browser')

    const sink = new VideoSampleSink(videoTrack)
    const frameDurationUs = Math.round(1_000_000 / OUT_FPS)

    const rangeStart = Math.max(0, windowStart - TRIM_EPSILON)
    const rangeEnd = windowEnd + TRIM_EPSILON

    const srcW = videoTrack.codedWidth
    const srcH = videoTrack.codedHeight
    const videoTrackInfo = `${videoTrack.codec} ${srcW}x${srcH}`
    const scale = Math.min(OUT_W / srcW, OUT_H / srcH)
    const dw = srcW * scale
    const dh = srcH * scale
    const dx = (OUT_W - dw) / 2
    const dy = (OUT_H - dh) / 2

    let firstTs: number | null = null
    let lastSampleEnd = 0
    let i = 0
    let skippedPreroll = 0
    try {
        for await (const sample of sink.samples(rangeStart, rangeEnd)) {
            // Skip samples that are before the window start (with a small epsilon to avoid floating point issues)
            if (sample.timestamp < windowStart - TRIM_EPSILON) {
                skippedPreroll++
                // log(`[WebCodecsExporter] pre-roll skip ts=${sample.timestamp.toFixed(3)} < windowStart=${windowStart}`)
                sample.close()
                continue
            }
            if (firstTs === null) {
                // log(`[WebCodecsExporter] firstTs=${sample.timestamp.toFixed(3)} (requested rangeStart=${rangeStart.toFixed(3)}), skippedPreroll=${skippedPreroll}`)
            }

            if (firstTs === null) firstTs = sample.timestamp

            // Backpressure: yield if the encoder queue is growing to avoid GPU memory buildup
            while (encoder.encodeQueueSize > 10) {
                await new Promise(r => setTimeout(r, 0))
            }

            const relativeUs = Math.round((sample.timestamp - firstTs) * 1_000_000)
            const outputTs = baseTimestampUs + Math.max(0, relativeUs)

            if (rangeEnd !== Infinity) {
                const windowProgressPct = Math.min(100, Math.max(0, Math.round(((sample.timestamp - rangeStart) / (rangeEnd - rangeStart)) * 100)))
                const sliceProgressPct = Math.round((((windowIndex - 1) + windowProgressPct / 100) / windowCount) * 100)
                const overallPct = Math.round(((sliceIndex - 1) / sliceCount) * 100 + (sliceProgressPct / sliceCount))
                onProgress(overallPct / 100, `Processing video slice ${sliceIndex} of ${sliceCount} (${sliceProgressPct}%)...`)
            } else {
                onProgress((sliceIndex - 1) / sliceCount, `Processing segment ${sliceIndex} of ${sliceCount}...`)
            }

            ctx.fillStyle = 'black'
            ctx.fillRect(0, 0, OUT_W, OUT_H)
            sample.draw(ctx, dx, dy, dw, dh)
            sample.close()

            const scaled = new VideoFrame(canvas, { timestamp: outputTs, duration: frameDurationUs })
            const isKeyFrame = (forceKeyFrame && i === 0) || i % GOP_SIZE === 0
            encoder.encode(scaled, { keyFrame: isKeyFrame })
            scaled.close()

            lastSampleEnd = sample.timestamp + sample.duration
            i++
        }
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        const wrapped = new Error(
            `WebCodecs decode failed for ${videoTrackInfo}, ` +
            `slice [${windowStart}-${windowEnd}], blobSize=${blob.size}, ` +
            `baseTimestampUs=${baseTimestampUs}: ${message}`
        )
        ;(wrapped as any).cause = error
        throw wrapped
    }

    if (firstTs === null) return 0
    return Math.round((lastSampleEnd - firstTs) * 1_000_000)
}

async function processSlice(
    blob: Blob,
    startSec: number,
    endSec: number,
    encoder: VideoEncoder,
    source: EncodedVideoPacketSource,
    segmentChunks: ChunkEntry[],
    baseTimestampUs: number,
    onProgress: ExportProgressFn,
    sliceIndex: number,
    sliceCount: number,
): Promise<number> {
    const canvas = new OffscreenCanvas(OUT_W, OUT_H)
    const ctx = canvas.getContext('2d')!

    let windowStart = startSec < 0 ? 0 : startSec
    const windowEnd = endSec < 0 ? Infinity : endSec
    let accumulatedUs = 0
    let isFirstWindow = true

    const windowCount = windowEnd === Infinity ? 1 : Math.max(1, Math.ceil((windowEnd - windowStart) / MAX_DECODE_CHUNK_SEC))
    let windowIndex = 0

    while (windowStart < windowEnd - TRIM_EPSILON) {
        const chunkEnd = Math.min(windowStart + MAX_DECODE_CHUNK_SEC, windowEnd)
        windowIndex += 1

        segmentChunks.length = 0
        const windowDurationUs = await decodeWindow(
            blob, windowStart, chunkEnd, encoder,
            baseTimestampUs + accumulatedUs,
            canvas, ctx,
            !isFirstWindow,
            onProgress,
            sliceIndex,
            sliceCount,
            startSec,
            endSec,
            windowIndex,
            windowCount,
        )

        await encoder.flush()
        for (const { chunk, meta } of segmentChunks) {
            await source.add(EncodedPacket.fromEncodedChunk(chunk), meta)
        }

        accumulatedUs += windowDurationUs
        windowStart = chunkEnd
        isFirstWindow = false
    }

    return accumulatedUs
}

export async function exportVideoWebCodecs(
    slices: VideoSlice[],
    getBlob: (slice: VideoSlice) => Promise<Blob>,
    onProgress: ExportProgressFn = () => {},
): Promise<File> {
    if (!webCodecsSupported()) {
        throw new Error('WebCodecs not supported — use Chrome 94+, Edge 94+, or Safari 26+')
    }

    const source = new EncodedVideoPacketSource('avc')
    const bufferTarget = new BufferTarget()
    const output = new Output({
        format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
        target: bufferTarget,
    })
    output.addVideoTrack(source)
    await output.start()

    const segmentChunks: ChunkEntry[] = []
    const encoder = new VideoEncoder({
        output: (chunk, meta) => segmentChunks.push({ chunk, meta }),
        error: (e) => { throw e },
    })
    encoder.configure({
        codec: 'avc1.42E028',
        width: OUT_W,
        height: OUT_H,
        bitrate: OUT_VIDEO_BPS,
        framerate: OUT_FPS,
        latencyMode: 'quality',
        avc: { format: 'avc' },
    })

    let baseTimestampUs = 0
    const nonEmpty = slices.filter(s => s.endPosition > s.position + TRIM_EPSILON)

    for (let i = 0; i < nonEmpty.length; i++) {
        const slice = nonEmpty[i]
        const pct = Math.round(((i + 1) / nonEmpty.length) * 100)
        onProgress((i + 1) / nonEmpty.length, `Processing segment ${i + 1} of ${nonEmpty.length} (${pct}%)...`)

        const blob = await getBlob(slice)
        const durationUs = await processSlice(
            blob, slice.position, slice.endPosition,
            encoder, source, segmentChunks, baseTimestampUs,
            onProgress, i + 1, nonEmpty.length,
        )
        baseTimestampUs += durationUs
    }

    encoder.close()
    await output.finalize()

    onProgress(1, '')
    return new File([bufferTarget.buffer!], 'sltt-video.mp4', { type: 'video/mp4' })
}
